/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import React from 'react';

import { cssMap, cx, jsx } from '@atlaskit/css';
import type { NewCoreIconProps } from '@atlaskit/icon/types';
import { Box } from '@atlaskit/primitives/compiled/box';
import { Inline } from '@atlaskit/primitives/compiled/inline';
import { Text } from '@atlaskit/primitives/compiled/text';
import { token } from '@atlaskit/tokens';

const styles = cssMap({
	metadata: {
		gridArea: 'metadata',
		marginTop: 'auto',
		minWidth: 0,
		paddingInline: token('space.200'),
		paddingBottom: token('space.200'),
	},
	interactive: {
		pointerEvents: 'auto',
	},
	row: {
		minWidth: 0,
		flexWrap: 'nowrap',
		overflow: 'hidden',
	},
	item: {
		minWidth: 0,
	},
});

const DEFAULT_ICON_COLOR = token('color.icon.subtlest');

export type CardMetadataItem = {
	key: string;
	icon?: React.ComponentType<NewCoreIconProps>;
	iconColor?: NewCoreIconProps['color'];
	label: React.ReactNode;
};

export type CardMetadataProps = {
	items?: CardMetadataItem[];
	children?: React.ReactNode;
	isInteractive?: boolean;
	testId?: string;
};

const Separator = (): React.JSX.Element => (
	<Text size="small" color="color.text.subtle">
		·
	</Text>
);

export const CardMetadata = ({
	items,
	children,
	isInteractive = false,
	testId,
}: CardMetadataProps): React.JSX.Element => (
	<Box xcss={cx(styles.metadata, isInteractive && styles.interactive)} testId={testId}>
		{items ? (
			<Inline xcss={styles.row} space="space.075" alignBlock="center">
				{items.map(({ key, icon: Icon, iconColor, label }, index) => (
					<React.Fragment key={key}>
						{index > 0 ? <Separator /> : null}
						<Inline xcss={styles.item} space="space.050" alignBlock="center">
							{Icon ? <Icon label="" size="small" color={iconColor ?? DEFAULT_ICON_COLOR} /> : null}
							<Text size="small" color="color.text.subtlest" maxLines={1}>
								{label}
							</Text>
						</Inline>
					</React.Fragment>
				))}
			</Inline>
		) : (
			children
		)}
	</Box>
);
